import { useRef, useState, useEffect } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const LIFECYCLE_IMAGE = "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/new-clc.png";
const IMAGE_CLASS = "h-auto object-contain max-w-[5500px] xxs:max-w-[4000px] xs:max-w-[4800px] sm:max-w-[5100px] md:max-w-[4800px] lg:max-w-[5500px] pointer-events-none flex-shrink-0";

const NORMAL_SPEED = 2;
const HOVER_SPEED = 0.5;

export default function CreatorLifeCycle() {
  const trackRef = useRef(null);
  const offsetXRef = useRef(0);
  const lastMouseXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isMountedRef = useRef(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Only run auto-scroll when the strip is in view (useScrollReveal with triggerOnce=false)
  const { ref: stripContainerRef, isVisible } = useScrollReveal(0.2, false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Reset to first image when section comes into view
  useEffect(() => {
    if (isVisible) {
      offsetXRef.current = 0;
      const track = trackRef.current;
      if (track) track.style.transform = "translateX(0px)";
    }
  }, [isVisible]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !isVisible) return;

    let rafId = null;
    const tick = () => {
      const width = track.scrollWidth / 2;
      if (width <= 0) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      if (!isDraggingRef.current) {
        const speed = isHovered ? HOVER_SPEED : NORMAL_SPEED;
        offsetXRef.current += speed;
        if (offsetXRef.current >= width) offsetXRef.current -= width;
      } else {
        if (offsetXRef.current >= width) offsetXRef.current -= width;
        if (offsetXRef.current < 0) offsetXRef.current += width;
      }
      track.style.transform = `translateX(${-offsetXRef.current}px)`;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isHovered, isVisible]);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    lastMouseXRef.current = e.clientX;
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const track = trackRef.current;
      if (!track) return;
      const width = track.scrollWidth / 2;
      if (width <= 0) return;
      const delta = e.clientX - lastMouseXRef.current;
      offsetXRef.current -= delta;
      while (offsetXRef.current >= width) offsetXRef.current -= width;
      while (offsetXRef.current < 0) offsetXRef.current += width;
      lastMouseXRef.current = e.clientX;
    };
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      if (isMountedRef.current) setIsDragging(false);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div id="start-earning" className="bg-[#ffffff] py-20 px-4 lg:px-6 xl:px-8  !z-0">
      <div className="5xl:max-w-[100rem] mx-auto">
      <div className="mb-8 px-8 xxs:px-6 max-w-7xl mx-auto">
        <p className="text-black/60 text-lg 4xl:text-[20px] font-medium mb-7 pt-4 enfinity-subtext-font text-[24px]">
          <span className="md:hidden text-sm">Creators journey with Enfinity</span>
          <span className="hidden md:inline enfinity-subtext-font">Our 17 years of creator economy data showed us a clear pattern.</span>
        </p>
        <h2
          className="text-4xl lg:text-[80px] xxs:text-[55px] 4xl:text-[90px] font-normal lg:mb-0 xl:mb-1 4xl:mb-1 pb-2 bg-clip-text text-transparent enfinity-header-font !leading-tight lg:leading-none"
          style={{
            backgroundImage: "linear-gradient(to right, #00D115 5%, #00B0FF 10%, #9C44E9 50%)",
          }}
        >
          Enfinity builds solutions
        </h2>
        <p className="text-2xl md:text-6xl lg:text-[80px] xxs:text-5xl xs:text-5xl sm:text-5xl 4xl:text-[90px] font-normal text-black enfinity-header-font !leading-normal lg:leading-none enfinity-header-font">
          that help creators thrive <span className="xl:block">at every stage</span>
        </p>
      </div>

      <div
        ref={stripContainerRef}
        className={`relative -mx-4 lg:-mx-6 xl:-mx-8 overflow-hidden select-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={handleMouseDown}
      >
        <div className="max-w-7xl xl:max-w-full mx-auto">
          <div className="relative pb-8">
            <div
              ref={trackRef}
              className="flex w-max will-change-transform"
              style={{ transform: "translateX(0)" }}
            >
              <img
                src={LIFECYCLE_IMAGE}
                alt="Creator Lifecycle"
                className={IMAGE_CLASS}
                draggable={false}
              />
              <img
                src={LIFECYCLE_IMAGE}
                alt=""
                aria-hidden
                className={`${IMAGE_CLASS} ml-20`}
                draggable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
